import { Alert, Drawer, Typography } from "@mui/material";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import { USER_PAGE_TOAST } from "common/Constants/UserPageToast.constants";
import { lazy, Suspense } from "react";
import { usePlatformUserAccessInitialization } from "./PlatformUserAccess.hook";
import { AccessStatusChip } from "common/Components/GenericForm/FieldTypes/AccessStatusSelect";
import UserAccessList from "./components/UserAccessList";
import InviteUserModal from "./components/InviteUserModal";
import UserAccessDetailPanel from "./components/UserAccessDetailPanel";
import {
  PLATFORM_USER_ACCESS_LAYOUT,
  PLATFORM_USER_ACCESS_UI,
} from "./PlatformUserAccess.constants";
import {
  DetailDrawerHeaderStack,
  DetailDrawerStatusChipWrap,
  DetailDrawerSubtitle,
  DetailDrawerTitle,
  DetailDrawerTitleRow,
  DrawerContentContainer,
  DrawerHeader,
  drawerPaperSx,
  ErrorAlertContainer,
  ToolbarClearFiltersButton,
  ToolbarInviteButton,
} from "./PlatformUserAccess.styles";
import {
  DEFAULT_PLATFORM_LIST_FILTERS,
} from "./PlatformUserAccess.constants";

const Toast = lazy(() => import("common/Components/Toast"));

const hasActivePlatformFilters = (filters) =>
  Object.keys(DEFAULT_PLATFORM_LIST_FILTERS).some(
    (key) => (filters?.[key] ?? "") !== ""
  );

const PlatformUserAccess = () => {
  const {
    state,
    customerRoleOptions,
    activeCustomers,
    actions,
    toast,
    dismissToast,
  } = usePlatformUserAccessInitialization();

  const platformRoleOptions =
    INFRAMARK_CONSTANTS?.users?.constants?.platformRoles ?? [];
  const filtersActive = hasActivePlatformFilters(state.listFilters);

  return (
    <>
      <Suspense fallback={null}>
        <Toast
          type={toast.type}
          title={toast.title}
          description={toast.description}
          show={toast.show}
          onClose={dismissToast}
          duration={USER_PAGE_TOAST.DURATION}
        />
      </Suspense>
      {state.error && (
        <ErrorAlertContainer>
          <Alert severity="error" onClose={actions.clearError}>
          {state.error}
          </Alert>
        </ErrorAlertContainer>
      )}
      <UserAccessList
        rows={state.users}
        loading={state.loading}
        pagination={state.pagination}
        listFilters={state.listFilters}
        platformRoleFilterOptions={platformRoleOptions}
        onListFiltersChange={actions.onListFiltersChange}
        onPageChange={actions.onPageChange}
        onRowsPerPageChange={actions.onRowsPerPageChange}
        onRowClick={actions.loadUserDetails}
        onRowAction={actions.onUserListAction}
        toolbarActions={
          <>
            <ToolbarClearFiltersButton
              variant="outlined"
              disabled={!filtersActive}
              onClick={actions.onClearFilters}
            >
              Clear Filters
            </ToolbarClearFiltersButton>
            <ToolbarInviteButton variant="outlined" onClick={actions.openInviteModal}>
              {PLATFORM_USER_ACCESS_UI.inviteUserLabel}
            </ToolbarInviteButton>
          </>
        }
      />
      <Drawer
        anchor="right"
        open={Boolean(state.selectedUserDetails)}
        onClose={actions.closeUserDetails}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <DrawerContentContainer
          $drawerWidth={PLATFORM_USER_ACCESS_LAYOUT.drawerWidth}
        >
          <DrawerHeader>
            <DetailDrawerHeaderStack spacing={0.5}>
              <DetailDrawerTitleRow>
                <DetailDrawerTitle variant="h6" component="span">
                  {PLATFORM_USER_ACCESS_UI.selectedUserTitle}
                </DetailDrawerTitle>
                <DetailDrawerStatusChipWrap>
                  <AccessStatusChip status={state?.selectedUserDetails?.status} />
                </DetailDrawerStatusChipWrap>
              </DetailDrawerTitleRow>
              <DetailDrawerSubtitle noWrap>
                {(() => {
                  const details = state?.selectedUserDetails;
                  const fullName = [details?.firstName, details?.lastName]
                    .map((part) => String(part ?? "").trim())
                    .filter(Boolean)
                    .join(" ");
                  if (fullName && details?.email) {
                    return `${fullName} — ${details.email}`;
                  }
                  return details?.email ?? details?.username ?? "";
                })()}
              </DetailDrawerSubtitle>
            </DetailDrawerHeaderStack>
          </DrawerHeader>
          <UserAccessDetailPanel
            selectedUser={state.selectedUserDetails}
            customers={activeCustomers}
            platformRoleOptions={platformRoleOptions}
            roleOptions={customerRoleOptions}
            onSave={actions.saveUserDetails}
            onCancel={actions.closeUserDetails}
            onAccessActionSuccess={actions.refreshAfterCustomerAccessAction}
            onCustomerUserListAction={actions.onCustomerUserListAction}
          />
        </DrawerContentContainer>
      </Drawer>
      <InviteUserModal
        open={state.isInviteModalOpen}
        onClose={actions.closeInviteModal}
        onSubmit={actions.inviteUser}
        checkInviteEmail={actions.checkInviteEmail}
        platformRoleOptions={platformRoleOptions}
        roleOptions={customerRoleOptions}
        customers={activeCustomers}
      />
    </>
  );
};

export default PlatformUserAccess;
